import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import PropertyCard from "@/components/property/PropertyCard";
import SearchBar from "@/components/common/SearchBar";
import LongStayNudge from "@/components/common/LongStayNudge";

const COLLECTIONS = [
  { slug: "all", label: "All" },
  { slug: "couples", label: "Couples" },
  { slug: "cinematic", label: "Cinematic" },
  { slug: "experiential-design", label: "Experiential Getaways" },
  { slug: "private-gatherings", label: "Private Gatherings" },
  { slug: "long-stays", label: "Long Stays" },
];

const LOCATIONS = [
  { slug: "all", label: "All locations" },
  { slug: "south-delhi", label: "Near Dhan Mill · South Delhi" },
  { slug: "gk1", label: "Greater Kailash · GK 1" },
  { slug: "gurugram", label: "Uppal Southend · Gurugram" },
];

export default function Stays() {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const active = params.get("collection") || "all";
  const guests = Number(params.get("guests") || 0);
  const checkIn = params.get("check_in") || "";
  const checkOut = params.get("check_out") || "";
  const loc = params.get("location") || "";

  useEffect(() => {
    const q = {};
    if (active && active !== "all") q.collection = active;
    if (guests) q.guests = guests;
    if (checkIn && checkOut) { q.start_date = checkIn; q.end_date = checkOut; }
    setLoading(true);
    api.get("/properties", { params: q })
      .then(({ data }) => {
        // Client-side location filter so we don't need a backend change: the
        // `location_slug` field is already returned on every property.
        const filtered = loc ? data.filter((p) => p.location_slug === loc) : data;
        setProperties(filtered);
      })
      .finally(() => setLoading(false));
  }, [active, guests, checkIn, checkOut, loc]);

  const setActive = (slug) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("collection");
    else next.set("collection", slug);
    setParams(next);
  };

  const setLocation = (slug) => {
    const next = new URLSearchParams(params);
    if (!slug || slug === "all") next.delete("location");
    else next.set("location", slug);
    setParams(next);
  };

  const datesApplied = Boolean(checkIn && checkOut);
  const nights = useMemo(() => {
    if (!datesApplied) return 0;
    const start = new Date(checkIn), end = new Date(checkOut);
    return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut, datesApplied]);
  const isLongStay = nights >= 30;
  const isLongStayCollection = active === "long-stays";

  return (
    <div data-testid="stays-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
      <Helmet>
        <title>All Stays · Vyagram Experiences</title>
        <meta name="description" content="Browse design-led direct-booking stays across South Delhi and Gurugram." />
      </Helmet>

      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="vyg-eyebrow">Portfolio</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">All Stays</h1>
          <p className="editorial-body mt-2">Curated homes. Direct booking. Real hospitality.</p>
        </div>
        <p className="text-sm text-noir/70">{loading ? "Checking live availability…" : `${properties.length} ${properties.length === 1 ? "stay" : "stays"}`}{datesApplied ? ` for your dates` : ""}</p>
      </div>

      <div className="mt-6"><SearchBar variant="inline" /></div>

      <div className="mt-6 -mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
        {COLLECTIONS.map((c) => (
          <button
            key={c.slug}
            onClick={() => setActive(c.slug)}
            data-testid={`filter-chip-${c.slug}`}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-all ${
              active === c.slug
                ? "border-forest bg-forest text-parchment"
                : "border-forest/20 bg-white text-noir/80 hover:border-forest/60"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Location filter row — separate from collections so the two can be combined */}
      <div className="mt-2 -mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0" data-testid="location-filter-row">
        {LOCATIONS.map((l) => {
          const isActive = (loc || "all") === l.slug;
          return (
            <button
              key={l.slug}
              onClick={() => setLocation(l.slug)}
              data-testid={`location-chip-${l.slug}`}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs transition-all ${
                isActive
                  ? "border-gold bg-gold/15 text-forest"
                  : "border-forest/10 bg-parchment text-noir/70 hover:border-forest/30"
              }`}
            >
              {l.label}
            </button>
          );
        })}
      </div>

      {(isLongStay || isLongStayCollection) && (
        <div className="mt-4">
          <LongStayNudge nights={nights} context={isLongStayCollection ? "collection" : "search"} />
        </div>
      )}

      {loading && (
        <div
          data-testid="stays-loading"
          className="mt-4 flex items-center gap-3 rounded-xl border border-forest/15 bg-parchment/80 px-4 py-3"
        >
          <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium text-forest">Checking live availability across {checkIn ? "the portfolio" : "all stays"}…</p>
            <div className="mt-2 h-0.5 w-full overflow-hidden rounded-full bg-forest/10">
              <span className="block h-full w-1/3 animate-shimmer bg-gradient-to-r from-transparent via-gold to-transparent" />
            </div>
          </div>
        </div>
      )}

      {datesApplied && !loading && (
        <div className="mt-4 rounded-xl border border-forest/15 bg-mist/50 px-4 py-2.5 text-xs text-forest" data-testid="dates-applied-banner">
          Showing availability for <strong>{checkIn}</strong> → <strong>{checkOut}</strong>. Stays that aren&apos;t open on these dates are tagged <em>Unavailable for these dates</em> — tap any card to pick other dates.
        </div>
      )}

      {properties.length === 0 && !loading ? (
        <div className="mt-16 rounded-2xl border border-dashed border-forest/20 bg-white/60 p-12 text-center">
          <p className="font-display text-2xl">No stays match your search.</p>
          <p className="editorial-body mt-2">Try different dates or talk to us — we can often help.</p>
          <Link to="/stays" className="vyg-link-gold mt-3 inline-flex">Clear filters →</Link>
        </div>
      ) : (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {properties.map((p) => (<PropertyCard key={p.slug} p={p} />))}
        </div>
      )}
    </div>
  );
}
